import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isCourseEvent } from '../lib/eventTypes';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useEvent } from '../contexts/EventContext';
import {
  Plus,
  Edit,
  Trash2,
  Dumbbell,
  Timer,
  Repeat,
  TrendingUp,
  Clock,
  Loader2,
  X,
  ChevronDown,
  ChevronRight,
  GitBranch,
  Pencil,
  Pause,
  Sparkles,
  GripVertical
} from 'lucide-react';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { refuseIfDateInputIncomplete } from '../lib/dateInputs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { cn } from '../lib/utils';
import { Switch } from '../components/ui/switch';
import WodPalier from '../components/wods/WodPalier';
import apiService from '../services/api.service';
import { toast } from 'sonner';
import { useRealtime } from '../contexts/RealtimeContext';
import { Event, WODForm, Category } from '../types';
import { WODStructureBuilder } from '../components/wod/WODStructureBuilder';
import { WodStructure, WodStructureUtils, WodPart, PartType, ExecutionMode, TrackingMode, WodElementType, WodGroup, WOD as ExtendedWOD } from '../types/wod.types';

const WODs: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Utiliser le contexte global pour l'événement sélectionné
  const { events, selectedEventId, selectedEvent } = useEvent();

  const [wods, setWods] = useState<ExtendedWOD[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // L'éditeur est une PAGE (LOG-218), plus une modale : /admin/wods/new ou
  // /admin/wods/:wodId/edit. La route dit ce qu'on édite ; l'état ci-dessous
  // est initialisé quand elle change.
  const { wodId: routeWodId } = useParams<{ wodId: string }>();
  const location = useLocation();
  const isEditorRoute = location.pathname.endsWith('/new') || Boolean(routeWodId);
  const editorRouteKey = routeWodId ? `edit:${routeWodId}` : (isEditorRoute ? 'new' : '');
  const initializedForRef = useRef<string>('');
  const [editingWod, setEditingWod] = useState<ExtendedWOD | null>(null);
  const [wodToDelete, setWodToDelete] = useState<ExtendedWOD | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  // Mode de l'éditeur : 'ai' = conversation WODEE (accueil d'un nouveau WOD), 'form' = prescription, 'course' = formulaire Course simplifié
  const [wodMode, setWodMode] = useState<'ai' | 'form' | 'course'>('ai');
  // Indique si le WOD est en mode parallèle (toggle A/B disponible)
  const [isParallelWod, setIsParallelWod] = useState(false);
  const [formData, setFormData] = useState<WODForm>({
    wod_number: 1,
    name: '',
    type: 'FOR TIME',
    time_cap: undefined,
    description: '',
    scoring_type: 'time',
  });
  const [wodStructure, setWodStructure] = useState<WodStructure>({ elements: [] });
  const [parts, setParts] = useState<WodPart[]>([]);
  const [editingPartIndex, setEditingPartIndex] = useState<number | null>(null);
  // Blocs dépliés dans l'éditeur : cliquer la ligne montre la structure
  // (mouvements, charges) sans ouvrir le dialogue d'édition.
  const [expandedParts, setExpandedParts] = useState<Set<number>>(new Set());
  const togglePartExpansion = (index: number) => {
    setExpandedParts(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };
  const [openPartDialog, setOpenPartDialog] = useState(false);

  // Category variations state
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryTab, setSelectedCategoryTab] = useState<string>('default');
  const [partsPerCategory, setPartsPerCategory] = useState<Record<string, WodPart[]>>({ default: [] });
  const [structuresPerCategory, setStructuresPerCategory] = useState<Record<string, WodStructure>>({ default: { elements: [] } });
  const [openAddCategoryDialog, setOpenAddCategoryDialog] = useState(false);
  // Variante source de la copie dans « Ajouter une variation » — choisie par
  // l'organisateur, jamais un « Défaut » qui n'existe plus.
  const [copySourceKey, setCopySourceKey] = useState<string>('');
  const [targetCategoryId, setTargetCategoryId] = useState<string>('');
  const [draggedVariantKey, setDraggedVariantKey] = useState<string | null>(null);

  // AI generation state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);
  // Conversation WODEE : même contrat que l'app Athlète (session côté API,
  // 30 min), WODEE reformule, l'organisateur corrige, puis valide.
  const [aiMessages, setAiMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [aiSessionId, setAiSessionId] = useState<string | undefined>(undefined);
  // Une feuille de WOD collée fait vite 25 lignes : au-delà de 12, le message
  // est replié avec « Voir plus », plutôt qu'un défilement interne invisible.
  const AI_MSG_COLLAPSE_LINES = 12;
  const [expandedAiMessages, setExpandedAiMessages] = useState<Set<number>>(new Set());
  // La réponse de WODEE arrive sous une feuille de tableau souvent longue :
  // on descend au dernier message, sinon elle reste hors de vue.
  const aiScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = aiScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [aiMessages, aiGenerating]);
  const [aiAvailable, setAiAvailable] = useState<boolean | null>(null);

  // État pour tracker les WODs multi-parties déployés
  const [expandedWods, setExpandedWods] = useState<Set<string>>(new Set());

  const toggleWodExpansion = (wodId: string) => {
    setExpandedWods(prev => {
      const newSet = new Set(prev);
      if (newSet.has(wodId)) {
        newSet.delete(wodId);
      } else {
        newSet.add(wodId);
      }
      return newSet;
    });
  };

  const [partFormData, setPartFormData] = useState<WodPart>({
    part_number: 1,
    name: '',
    type: PartType.FOR_TIME,
    time_cap_minutes: 6,
    // duration_seconds supprimé - uniquement pour type REST
    description: '',
    max_points: 75,
    scoring_type: 'time',
    is_mandatory: true,
    structure: { elements: [] },
    execution_mode: ExecutionMode.SEQUENTIAL,
    tracking_mode: TrackingMode.LIVE,
    parallel_group: undefined
  });

  useEffect(() => {
    if (selectedEventId) {
      setLoading(true);
      Promise.all([loadWods(), loadCategories()]).finally(() => setLoading(false));
    }
  }, [selectedEventId]);

  // Lien profond ?create=true (tableau de bord) → la page de création
  useEffect(() => {
    const shouldCreate = searchParams.get('create');
    if (shouldCreate === 'true' && selectedEventId && !isEditorRoute) {
      setSearchParams(prev => {
        prev.delete('create');
        return prev;
      });
      navigate('/admin/wods/new');
    }
  }, [searchParams, selectedEventId, isEditorRoute]);

  // Initialise l'éditeur quand la route change (nouveau, ou WOD à éditer une
  // fois la liste chargée) — une seule fois par route.
  useEffect(() => {
    if (!isEditorRoute || !selectedEventId) return;
    if (initializedForRef.current === editorRouteKey) return;
    if (routeWodId) {
      const target = wods.find(w => w.id === routeWodId);
      if (!target) return; // liste pas encore chargée
      initializedForRef.current = editorRouteKey;
      initEditor(target);
    } else {
      initializedForRef.current = editorRouteKey;
      initEditor();
    }
  }, [isEditorRoute, editorRouteKey, routeWodId, wods, selectedEventId, categories]);
  useEffect(() => {
    if (!isEditorRoute) initializedForRef.current = '';
  }, [isEditorRoute]);

  const loadCategories = async () => {
    if (!selectedEventId) return;
    try {
      const response = await apiService.getCategories(selectedEventId);
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  // Check AI availability on mount
  useEffect(() => {
    apiService.getAiStatus()
      .then(data => setAiAvailable(data.available))
      .catch(() => setAiAvailable(false));
  }, []);

  // AI WOD generation handler
  /// Rapproche un libellé de variante écrit au tableau (« Rx », « Sc »,
  /// « Int », « Masters »…) d'une catégorie de l'événement — égalité,
  /// alias usuels, ou préfixe non ambigu.
  const matchCategoryForLabel = (label: string, cats: Category[]): Category | undefined => {
    const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const ALIASES: string[][] = [
      ['rx', "rx'd", 'rxd'],
      ['scaled', 'sc', 'scale'],
      ['inter', 'int', 'intermediate', 'intermediaire'],
      ['masters', 'master'],
      ['femmes', 'femme', 'women', 'woman', 'f'],
      ['hommes', 'homme', 'men', 'man', 'h'],
      ['debutant', 'debutants', 'beginner', 'beginners', 'rookie'],
      ['elite'], ['open'], ['teens', 'teen', 'ados', 'ado'],
    ];
    const l = norm(label);
    const group = ALIASES.find(g => g.includes(l));
    const exact = cats.find(c => norm(c.name) === l);
    if (exact) return exact;
    if (group) {
      const byAlias = cats.filter(c => group.includes(norm(c.name)));
      if (byAlias.length === 1) return byAlias[0];
    }
    // « Team Rx », « Double Scaled » : le libellé (ou un alias) est un MOT du nom.
    const tokens = (s: string) => norm(s).split(/[\s/·–-]+/).filter(Boolean);
    const byToken = cats.filter(c => tokens(c.name).some(tok => tok === l || (group ? group.includes(tok) : false)));
    if (byToken.length === 1) return byToken[0];
    const byPrefix = cats.filter(c => norm(c.name).startsWith(l) || l.startsWith(norm(c.name)));
    return byPrefix.length === 1 ? byPrefix[0] : undefined;
  };

  /// Pré-remplit le formulaire depuis un WOD généré (chat validé).
  const applyGeneratedWod = (wod: any, warnings: string[]) => {
      // Le générateur signale ce qu'il a corrigé ou ce qui lui paraît douteux
      // (ex. des parts nommées comme des catégories) : à montrer, pas à taire.
      setAiWarnings(warnings);
      // Pre-fill form data
      setFormData(prev => ({
        ...prev,
        name: wod.wod_name || '',
        type: wod.wod_type || 'FOR TIME',
        scoring_type: wod.scoring_type || 'time',
        time_cap: wod.time_cap_minutes || undefined,
        description: wod.description || '',
      }));
      // Pre-fill parts (default + category variants)
      const mapParts = (rawParts: any[]): WodPart[] =>
        (rawParts || []).map((p: any) => ({
          ...p,
          type: p.type as PartType,
          execution_mode: (p.execution_mode as ExecutionMode) || ExecutionMode.SEQUENTIAL,
          tracking_mode: TrackingMode.LIVE,
        }));

      const generatedParts = mapParts(wod.parts?.default || Object.values(wod.parts || {})[0]);

      // Chaque variante porte le libellé écrit par l'organisateur (« Rx »,
      // « Int », « Sc »), la première comprise — "default" n'existe que pour
      // une version unique. On rapproche chaque libellé d'une catégorie de
      // l'événement ; un libellé sans catégorie reste tel quel et l'onglet
      // propose de l'assigner (la table wod_parts ignore une clé qui n'est
      // ni "default" ni un id de catégorie : sans assignation, la variante
      // serait invisible au scoring).
      const newPartsPerCategory: Record<string, WodPart[]> = {};
      const taken = new Set<string>();
      Object.entries(wod.parts || {}).forEach(([key, variantParts]: [string, any]) => {
        const mapped = mapParts(variantParts);
        if (key === 'default') {
          newPartsPerCategory['default'] = mapped;
          return;
        }
        const match = matchCategoryForLabel(key, (categories || []).filter(c => !taken.has(c.id)));
        const resolvedKey = match ? match.id : key;
        if (match) taken.add(match.id);
        newPartsPerCategory[resolvedKey] = mapped;
      });
      if (Object.keys(newPartsPerCategory).length === 0) newPartsPerCategory['default'] = generatedParts;

      const firstKey = Object.keys(newPartsPerCategory)[0] || 'default';
      setParts(newPartsPerCategory[firstKey] || []);
      setPartsPerCategory(newPartsPerCategory);
      setSelectedCategoryTab(firstKey);
      // Detect parallel mode
      const hasParallel = (newPartsPerCategory[firstKey] || []).some(p => p.execution_mode === ExecutionMode.PARALLEL);
      setIsParallelWod(hasParallel);
      // Switch to form mode
      setWodMode('form');
  };

  // WODEE répond en Markdown léger (« **Titre :** ») : on rend le gras, le
  // reste (puces, retours à la ligne) passe tel quel en pre-line.
  const renderChatText = (content: string): React.ReactNode =>
    content.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
      chunk.startsWith('**') && chunk.endsWith('**')
        ? <strong key={i}>{chunk.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{chunk}</React.Fragment>
    );

  const handleAiSend = async () => {
    const text = aiPrompt.trim();
    if (!text) return;
    setAiGenerating(true);
    setAiError('');
    setAiPrompt('');
    setAiMessages(prev => [...prev, { role: 'user', content: text }]);
    try {
      const data = await apiService.chatWod({ session_id: aiSessionId, message: text, action: 'message' });
      setAiSessionId(data.session_id);
      if (data.error) {
        setAiError(data.error);
        return;
      }
      if (data.reply) setAiMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      if (data.is_final && data.wod) applyGeneratedWod(data.wod, Array.isArray(data.warnings) ? data.warnings : []);
    } catch (err: any) {
      setAiError(err?.response?.data?.detail || t('ai.generateError'));
    } finally {
      setAiGenerating(false);
    }
  };

  // Photo du WOD : même chemin que le texte, l'endpoint la lit déjà.
  const handleAiPhoto = async (file: File) => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    const imageType = (['image/jpeg', 'image/png', 'image/webp', 'image/heic'].includes(file.type) ? file.type : 'image/jpeg') as string;
    const text = aiPrompt.trim();
    setAiGenerating(true);
    setAiError('');
    setAiPrompt('');
    setAiMessages(prev => [...prev, { role: 'user', content: text ? `📷 ${t('ai.photoSent')}\n${text}` : `📷 ${t('ai.photoSent')}` }]);
    try {
      const data = await apiService.chatWod({ session_id: aiSessionId, message: text || undefined, image: base64, image_type: imageType, action: 'message' });
      setAiSessionId(data.session_id);
      if (data.error) {
        setAiError(data.error);
        return;
      }
      if (data.reply) setAiMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      if (data.is_final && data.wod) applyGeneratedWod(data.wod, Array.isArray(data.warnings) ? data.warnings : []);
    } catch (err: any) {
      setAiError(err?.response?.data?.detail || t('ai.generateError'));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAiValidate = async () => {
    setAiGenerating(true);
    setAiError('');
    try {
      const data = await apiService.chatWod({ session_id: aiSessionId, action: 'validate' });
      setAiSessionId(data.session_id);
      if (data.wod) {
        applyGeneratedWod(data.wod, Array.isArray(data.warnings) ? data.warnings : []);
      } else {
        setAiError(data.error || t('ai.generateError'));
      }
    } catch (err: any) {
      setAiError(err?.response?.data?.detail || t('ai.generateError'));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAiReset = () => {
    setAiMessages([]);
    setExpandedAiMessages(new Set());
    setAiSessionId(undefined);
    setAiPrompt('');
    setAiError('');
    setAiWarnings([]);
  };

  // Les événements sont chargés depuis EventContext

  // Temps réel (LOG-201). `wod_part` autant que `wod` : la structure d'une
  // épreuve vit dans une table à part depuis LOG-170, et c'est elle qui bouge
  // quand on édite les mouvements.
  useRealtime(['wod', 'wod_part'], () => { loadWods(); });

  const loadWods = async () => {
    if (!selectedEventId) return;

    try {
      const response = await apiService.getWODs(selectedEventId);
      const wods = response.data.wods || [];
      const processedWods = wods.map((wod: any) => ({
        ...wod,
        description: wod.description || ''
      }));
      setWods(processedWods);
    } catch (err: any) {
      setError(t('errors.generic'));
      console.error('Erreur chargement WODs:', err);
    }
  };

  // Les variantes d'un WOD : { "default" | category_id: parts[] } — un tableau
  // legacy vaut la version unique.
  /** Les clés d'un JSONB n'ont pas d'ordre : on applique `variant_order`
      (clés connues d'abord, dans cet ordre ; les autres suivent, telles que reçues). */
  const orderVariantKeys = (keys: string[], order?: string[] | null): string[] => {
    if (!order || order.length === 0) return keys;
    const known = order.filter(k => keys.includes(k));
    return [...known, ...keys.filter(k => !known.includes(k))];
  };

  const reorderRecord = (rec: Record<string, WodPart[]>, order: string[]): Record<string, WodPart[]> => {
    const out: Record<string, WodPart[]> = {};
    orderVariantKeys(Object.keys(rec), order).forEach(k => { out[k] = rec[k]; });
    return out;
  };

  const getVariantEntries = (wod: any): [string, WodPart[]][] => {
    const parts = wod?.parts;
    if (Array.isArray(parts)) return [['default', parts as WodPart[]]];
    if (parts && typeof parts === 'object') {
      const keys = orderVariantKeys(Object.keys(parts), wod?.variant_order);
      return keys
        .map(k => [k, parts[k]] as [string, WodPart[]])
        .filter(([, v]) => Array.isArray(v) && (v as any[]).length > 0);
    }
    return [];
  };

  /** Lecture en colonnes d'un WOD déplié (mockup B validé le 2026-09-09) :
      une ligne par partie (seulement s'il y en a plusieurs — sinon elle
      répéterait la ligne du WOD), et pour chaque partie une colonne par
      catégorie ; deux catégories dont la partie est strictement identique
      (structure, type, time cap) partagent la colonne. */
  const buildVariantRows = (wod: any) => {
    const entries = getVariantEntries(wod);
    const maxParts = entries.reduce((m, [, parts]) => Math.max(m, parts.length), 0);
    const rows: {
      index: number;
      multiPart: boolean;
      showLabels: boolean;
      part: WodPart | undefined;
      cols: { sig: string; labels: string[]; part: WodPart; preview: string }[];
    }[] = [];
    for (let i = 0; i < maxParts; i++) {
      const cols: { sig: string; labels: string[]; part: WodPart; preview: string }[] = [];
      entries.forEach(([key, parts]) => {
        const part = parts[i];
        if (!part) return;
        const isRest = part.type === PartType.REST;
        const preview = !isRest && part.structure && part.structure.elements?.length
          ? WodStructureUtils.generatePreview(part.structure)
          : '';
        const sig = `${part.type}|${part.time_cap_minutes ?? ''}|${part.duration_seconds ?? ''}|${preview}|${part.description ?? ''}`;
        const same = cols.find(c => c.sig === sig);
        if (same) same.labels.push(getCategoryName(key));
        else cols.push({ sig, labels: [getCategoryName(key)], part, preview });
      });
      rows.push({
        index: i,
        multiPart: maxParts > 1,
        showLabels: entries.length > 1,
        part: entries.map(([, parts]) => parts[i]).find(Boolean),
        cols,
      });
    }
    return { rows };
  };

  const denormalizeWodType = (type: string): string => {
    return type.toUpperCase().replace(/_/g, ' ');
  };

  // Pour les WODs multi-parties : calcule le time cap total des parties
  // - Parties parallèles (même parallel_group) → MAX des time_caps
  // - Parties séquentielles → SOMME des time_caps
  // parts est maintenant un dict { "default": [...], "category_id": [...] }
  const calculateTotalTimeCap = (parts: WodPart[]): number => {
    if (!parts || parts.length === 0) return 0;

    const groups: Record<number, number[]> = {}; // parallel_group -> list of time_caps
    const sequentialParts: number[] = [];

    parts.forEach(part => {
      // Gérer les pauses (REST)
      if (part.type === PartType.REST) {
        const durationMin = Math.ceil((part.duration_seconds || 0) / 60);
        sequentialParts.push(durationMin);
        return;
      }

      const timeCap = part.time_cap_minutes || 0;
      if (!timeCap) return;

      const executionMode = part.execution_mode || ExecutionMode.SEQUENTIAL;
      const parallelGroup = part.parallel_group;

      if (executionMode === ExecutionMode.PARALLEL && parallelGroup != null) {
        if (!groups[parallelGroup]) {
          groups[parallelGroup] = [];
        }
        groups[parallelGroup].push(timeCap);
      } else {
        sequentialParts.push(timeCap);
      }
    });

    // Total = MAX de chaque groupe parallèle + SOMME des parties séquentielles
    let total = 0;
    Object.values(groups).forEach(groupTimeCaps => {
      total += Math.max(...groupTimeCaps);
    });
    total += sequentialParts.reduce((sum, tc) => sum + tc, 0);

    return total;
  };

  // Récupérer les parties principales d'un WOD (première clé, que ce soit 'default' ou un category ID)
  const getPrimaryParts = (wod: ExtendedWOD): WodPart[] => {
    if (!wod.parts) return [];
    const keys = Object.keys(wod.parts);
    if (keys.length === 0) return [];
    return (wod.parts as Record<string, WodPart[]>)[keys[0]] || [];
  };

  const getDisplayTimeCap = (wod: ExtendedWOD): number | undefined => {
    const defaultParts = getPrimaryParts(wod);
    if (defaultParts.length > 1) {
      const total = calculateTotalTimeCap(defaultParts);
      return total > 0 ? total : undefined;
    }
    // WOD simple ou 1 partie
    if (defaultParts.length === 1) {
      return defaultParts[0].time_cap_minutes;
    }
    return wod.time_cap;
  };

  // Pour les WODs multi-parties : retourne les types uniques des parties (sauf REST)
  const getDisplayTypes = (wod: ExtendedWOD): string[] => {
    const defaultParts = getPrimaryParts(wod);
    if (defaultParts.length > 1) {
      const types = defaultParts
        .filter(p => p.type !== PartType.REST)
        .map(p => p.type.toUpperCase().replace(/_/g, ' '));
      // Retourner types uniques
      return Array.from(new Set(types));
    }
    // WOD simple ou 1 partie
    if (defaultParts.length === 1) {
      return [defaultParts[0].type.toUpperCase().replace(/_/g, ' ')];
    }
    return [wod.type];
  };

  // Pour les WODs multi-parties : retourne les scorings uniques des parties (sauf REST)
  const getDisplayScorings = (wod: ExtendedWOD): string[] => {
    const defaultParts = getPrimaryParts(wod);
    if (defaultParts.length > 1) {
      const scorings = defaultParts
        .filter(p => p.type !== PartType.REST && p.scoring_type)
        .map(p => p.scoring_type || 'time');
      // Retourner scorings uniques
      return Array.from(new Set(scorings));
    }
    // WOD simple ou 1 partie
    if (defaultParts.length === 1 && defaultParts[0].scoring_type) {
      return [defaultParts[0].scoring_type];
    }
    return [wod.scoring_type];
  };

  const handleOpenDialog = (wod?: ExtendedWOD) => {
    navigate(wod?.id ? `/admin/wods/${wod.id}/edit` : '/admin/wods/new');
  };

  const initEditor = (wod?: ExtendedWOD) => {
    handleAiReset();
    // Déterminer si c'est un événement Course
    const eventIsCourse = isCourseEvent(selectedEvent?.event_type);

    if (wod) {
      // Édition d'un WOD existant
      setEditingWod(wod);
      setFormData({
        wod_number: wod.wod_number,
        name: wod.name,
        type: denormalizeWodType(wod.type) as WODForm['type'],
        time_cap: wod.time_cap,
        description: wod.description || '',
        scoring_type: wod.scoring_type,
        requires_finished_wod_id: (wod as any).requires_finished_wod_id || null
      });

      // Charger les parties - tous les WODs sont multi-parties
      // Les clés peuvent être 'default' ou des category IDs (UUIDs)
      const partsKeys = orderVariantKeys(Object.keys(wod.parts || {}), (wod as any).variant_order);
      const firstKey = partsKeys[0] || 'default';
      const primaryParts = wod.parts?.[firstKey] || [];

      // Si le WOD n'a pas de parties, en créer une par défaut depuis les données du WOD
      if (primaryParts.length === 0) {
        const defaultPart: WodPart = {
          part_number: 1,
          name: wod.name || 'Partie 1',
          type: (wod.type?.toLowerCase().replace(/ /g, '_') as PartType) || PartType.FOR_TIME,
          time_cap_minutes: wod.time_cap,
          description: wod.description || '',
          scoring_type: wod.scoring_type || 'time',
          structure: wod.structure as WodStructure || { elements: [] },
          execution_mode: ExecutionMode.SEQUENTIAL,
          tracking_mode: TrackingMode.LIVE
        };
        setParts([defaultPart]);
        setPartsPerCategory({ default: [defaultPart] });
      } else {
        // Charger toutes les variations de catégories
        if (wod.parts) {
          setPartsPerCategory(reorderRecord(wod.parts as Record<string, WodPart[]>, (wod as any).variant_order || []));
        } else {
          setPartsPerCategory({ [firstKey]: primaryParts });
        }
        setParts(primaryParts);
      }

      // Sélectionner le premier tab disponible (default ou la première variante)
      setSelectedCategoryTab(firstKey);

      // Détecter si c'est un WOD parallèle (au moins une partie en mode parallèle)
      const hasParallelParts = primaryParts.some(p => p.execution_mode === ExecutionMode.PARALLEL);
      setIsParallelWod(hasParallelParts);

      // Structure pour le WOD (utilisée comme référence)
      if (wod.structure) {
        setWodStructure(wod.structure as WodStructure);
      } else {
        setWodStructure({ elements: [] });
      }

      // Édition: aller directement au formulaire (ou course pour événement Course)
      setWodMode(eventIsCourse ? 'course' : 'form');
    } else {
      // Création d'un nouveau WOD/Course
      setEditingWod(null);
      setIsParallelWod(false);
      const nextWodNumber = Math.max(...wods.map(w => w.wod_number), 0) + 1;
      setFormData({
        wod_number: nextWodNumber,
        name: '',
        type: 'FOR TIME', // Forcé pour les Courses
        time_cap: undefined,
        description: '',
        scoring_type: 'time', // Forcé pour les Courses
        requires_finished_wod_id: null
      });
      setWodStructure({ elements: [] });

      // Une course n'a qu'une seule partie, et aucune variante de catégorie.
      //
      // Le formulaire en créait une par catégorie — jusqu'à treize onglets —
      // pour y saisir une « description de parcours » et un time cap. Rien ne
      // les lisait : la génération de vagues ne consulte pas la table `wods`,
      // la page publique ne reçoit pas `parts`, et le chrono d'une course
      // mesure un temps sans jamais le borner. Sur les événements réels, ces
      // champs contenaient le libellé auto-généré et le défaut de 120 min,
      // jamais touchés.
      //
      // Ce qui dit quelle catégorie court quelle course, ce sont les VAGUES
      // (`heats.wod_id` + `heats.category_id`) — la seule déclaration lue.
      if (eventIsCourse) {
        const defaultPart: WodPart = {
          part_number: 1,
          name: 'Course',
          type: PartType.FOR_TIME,
          time_cap_minutes: undefined,
          description: '',
          scoring_type: 'time',
          structure: { elements: [] },
          execution_mode: ExecutionMode.SEQUENTIAL,
          tracking_mode: TrackingMode.LIVE
        };
        setParts([defaultPart]);
        setPartsPerCategory({ default: [defaultPart] });
        setWodMode('course');
      } else {
        // WOD CrossFit : WODEE d'abord (le builder vide est à un lien), la
        // question « séquentiel ou parallèle » est un réglage de bloc.
        setWodMode(aiAvailable === false ? 'form' : 'ai');
        const defaultPart: WodPart = {
          part_number: 1,
          name: '',
          type: PartType.FOR_TIME,
          time_cap_minutes: undefined,
          description: '',
          scoring_type: 'time',
          structure: { elements: [] },
          execution_mode: ExecutionMode.SEQUENTIAL,
          tracking_mode: TrackingMode.LIVE
        };
        setParts([defaultPart]);
        setPartsPerCategory({ default: [defaultPart] });
      }

      setStructuresPerCategory({ default: { elements: [] } });
      setSelectedCategoryTab('default');
    }
  };

  const handleCloseDialog = () => {
    setEditingWod(null);
    setWodMode('ai');
    setIsParallelWod(false);
    setError('');
    handleAiReset();
    navigate('/admin/wods');
  };

  // Séquentiel / parallèle : un réglage des blocs, appliqué aux blocs déjà
  // saisis pour que l'écran et l'enregistrement disent la même chose.
  const setParallelMode = (parallel: boolean) => {
    setIsParallelWod(parallel);
    setParts(prev => prev.map(p => ({
      ...p,
      execution_mode: parallel ? ExecutionMode.PARALLEL : ExecutionMode.SEQUENTIAL,
      parallel_group: parallel ? 1 : undefined,
    })));
  };

  // Classement sur le total du WOD : les parties de charge (Athlète 1 →
  // repos → Athlète 2) se classent ensemble sur la somme des charges de
  // l'équipe, au lieu de chacune de son côté. Un réglage du WOD, posé sur
  // toutes ses parties de charge et dans toutes les variantes.
  const chargeParts = parts.filter(p => p.type === 'max_load');
  const classementTotal = chargeParts.length >= 2 && chargeParts.every(p => p.team_total);
  const setClassementTotal = (total: boolean) => {
    const appliquer = (liste: WodPart[]) => liste.map(p =>
      p.type === 'max_load' ? { ...p, team_total: total || undefined } : p);
    setParts(prev => appliquer(prev));
    setPartsPerCategory(prev => Object.fromEntries(
      Object.entries(prev).map(([k, liste]) => [k, appliquer(liste)])));
  };

  // Couverture des catégories par les variantes (LOG-218) — affichée en
  // permanence au-dessus des blocs, et confirmée à l'enregistrement.
  const coverage = (() => {
    const keys = Object.keys({ ...partsPerCategory, [selectedCategoryTab]: parts }).filter(k => (partsPerCategory[k] || parts).length > 0 || k === selectedCategoryTab);
    const categoryKeys = keys.filter(k => k !== 'default');
    const hasDefault = keys.includes('default');
    const uncovered = (categories || []).filter(c => !categoryKeys.includes(c.id));
    return { categoryKeys, hasDefault, uncovered };
  })();

  // Gérer le changement de tab de catégorie
  const handleCategoryTabChange = (newTab: string) => {
    // Sauvegarder les parties du tab courant, charger celles du nouveau tab
    setPartsPerCategory(prev => ({
      ...prev,
      [selectedCategoryTab]: parts
    }));
    setSelectedCategoryTab(newTab);
    setParts(partsPerCategory[newTab] || []);
  };

  // Ajouter une variation pour une catégorie
  const handleAddCategoryVariation = (categoryId: string, copyFromKey: string | null) => {
    // Copier depuis la variante CHOISIE (la variante ouverte porte l'état
    // courant `parts`, pas encore reversé dans partsPerCategory), ou partir vide.
    const source = copyFromKey === null
      ? []
      : copyFromKey === selectedCategoryTab ? parts : (partsPerCategory[copyFromKey] || []);
    const newParts = JSON.parse(JSON.stringify(source));
    setPartsPerCategory(prev => ({
      ...prev,
      [selectedCategoryTab]: parts, // Sauvegarder le tab actuel
      [categoryId]: newParts
    }));
    setSelectedCategoryTab(categoryId);
    setParts(newParts);
    setOpenAddCategoryDialog(false);
  };

  /** Glisser un onglet de variante sur un autre : il prend sa place. L'ordre
      des clés de `partsPerCategory` EST l'ordre affiché — et il part au
      serveur dans `variant_order` à l'enregistrement. */
  const handleReorderVariant = (fromKey: string, toKey: string) => {
    if (fromKey === toKey) return;
    const keys = Object.keys(partsPerCategory);
    const from = keys.indexOf(fromKey);
    const to = keys.indexOf(toKey);
    if (from < 0 || to < 0) return;
    keys.splice(from, 1);
    keys.splice(to, 0, fromKey);
    setPartsPerCategory(prev => reorderRecord({ ...prev, [selectedCategoryTab]: parts }, keys));
  };

  // Supprimer une variation de catégorie
  const handleRemoveCategoryVariation = (categoryId: string) => {
    const keys = Object.keys(partsPerCategory);
    if (keys.length <= 1) return; // Ne pas supprimer la dernière variante

    setPartsPerCategory(prev => {
      const newParts = { ...prev };
      delete newParts[categoryId];
      return newParts;
    });
    // Basculer vers la première variante restante
    const remainingKeys = keys.filter(k => k !== categoryId);
    const fallbackKey = remainingKeys[0] || 'default';
    setSelectedCategoryTab(fallbackKey);
    setParts(partsPerCategory[fallbackKey] || []);
  };

  // Vérifie si on a des variations de catégorie
  const hasVariations = (): boolean => {
    return Object.keys(partsPerCategory || {}).length > 1;
  };

  // Retourne les clés de catégories
  const getCategoryKeys = (): string[] => {
    return Object.keys(partsPerCategory || { default: [] });
  };

  // Obtenir le nom d'une catégorie par ID
  const getCategoryName = (categoryId: string): string => {
    if (categoryId === 'default') return t('wods.categoryVariations.default');
    const category = (categories || []).find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  // Une clé de variante qui n'est pas un id de catégorie : "default", ou un
  // libellé écrit au tableau que WODEE n'a pas pu rapprocher.
  const isUnassignedVariantKey = (key: string): boolean =>
    key === 'default' || !(categories || []).some(c => c.id === key);

  // Assigner une variante (default ou libellé) à une vraie catégorie
  const handleAssignVariantKey = (oldKey: string, newCategoryId: string) => {
    if (!partsPerCategory[oldKey]) return;

    setPartsPerCategory(prev => {
      const newParts = { ...prev };
      newParts[newCategoryId] = prev[oldKey] || [];
      delete newParts[oldKey];
      return newParts;
    });

    if (selectedCategoryTab === oldKey) {
      setSelectedCategoryTab(newCategoryId);
    }
  };

  // Couverture explicite des catégories : quand un WOD a des variantes, une
  // catégorie sans variante ne reçoit RIEN (aucun repli entre variantes, ni
  // la première ni la plus haute — décision Bryan 2026-09-08). On le dit
  // avant d'enregistrer, au lieu de le découvrir le jour J sur le Judge.
  const [coverageConfirm, setCoverageConfirm] = useState<{ parts: Record<string, WodPart[]>; message: React.ReactNode } | null>(null);

  const handleSubmit = async () => {
    if (!selectedEventId) return;

    // Un champ date / heure à moitié saisi (heure sans date…) aurait une
    // valeur vide : on refuse en le disant plutôt qu'enregistrer en silence.
    if (refuseIfDateInputIncomplete(t)) return;

    // Synchroniser le tab courant dans partsPerCategory
    const finalPartsPerCategory = {
      ...partsPerCategory,
      [selectedCategoryTab]: parts
    };

    // Nettoyer les catégories vides
    const cleanedParts: Record<string, WodPart[]> = {};
    Object.entries(finalPartsPerCategory).forEach(([key, value]) => {
      if (value && value.length > 0) {
        cleanedParts[key] = value;
      }
    });
    // S'assurer qu'il y a au moins une entrée
    if (Object.keys(cleanedParts).length === 0) {
      cleanedParts['default'] = [];
    }
    // Une course n'a pas de variantes : les gardes ci-dessous protègent le
    // scoring CrossFit, où une variante mal nommée ou une catégorie non
    // couverte rend le WOD invisible au juge. Rien de tel ici — et surtout
    // plus aucun onglet pour y répondre depuis que le bloc « configuration
    // par catégorie » a été retiré. Les parts déjà en base sur les courses
    // existantes repartent telles quelles, sans être touchées.
    if (wodMode === 'course') {
      await performSave(cleanedParts);
      return;
    }

    // Une variante sous un libellé (« Sc ») et non un id de catégorie serait
    // ignorée par wod_parts, donc invisible au scoring : on refuse.
    const orphanKeys = Object.keys(cleanedParts).filter(k => k !== 'default' && !(categories || []).some(c => c.id === k));
    if (orphanKeys.length > 0) {
      toast.error(t('wods.categoryVariations.unassignedVariants', { labels: orphanKeys.join(', ') }));
      return;
    }

    const categoryKeys = Object.keys(cleanedParts).filter(k => k !== 'default');
    if (categoryKeys.length > 0) {
      const uncovered = (categories || []).filter(c => !categoryKeys.includes(c.id));
      if (uncovered.length > 0) {
        const names = uncovered.map(c => c.name).join(', ');
        setCoverageConfirm({
          parts: cleanedParts,
          message: 'default' in cleanedParts
            ? t('wods.categoryVariations.coverageDefault', { names })
            : t('wods.categoryVariations.coverageMissing', { names }),
        });
        return;
      }
    }

    await performSave(cleanedParts);
  };

  const performSave = async (cleanedParts: Record<string, WodPart[]>) => {
    if (!selectedEventId) return;
    setSaving(true);
    try {
      // Utiliser la description du formulaire (pour les Courses, elle est saisie séparément)
      const mainDescription = formData.description;

      const wodData = {
        ...formData,
        description: mainDescription,
        event_id: selectedEventId,
        structure: undefined, // Structure gérée par parties
        movements: [],
        parts: cleanedParts,
        variant_order: Object.keys(cleanedParts)
      };

      if (editingWod && editingWod.id) {
        await apiService.updateWOD(editingWod.id, wodData);
      } else {
        await apiService.createWOD(wodData);
      }

      handleCloseDialog();
      loadWods();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors
        setError(detail.map((e: any) => e.msg || JSON.stringify(e)).join(', '));
      } else if (detail) {
        setError(JSON.stringify(detail));
      } else {
        setError(t('errors.generic'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (wod: ExtendedWOD) => {
    if (!wod.id) return;
    setWodToDelete(wod);
  };

  const confirmDelete = async () => {
    if (!wodToDelete?.id) return;
    setDeleting(true);
    try {
      await apiService.deleteWOD(wodToDelete.id);
      setWodToDelete(null);
      loadWods();
    } catch (err: any) {
      setError(t('errors.generic'));
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenPartDialog = (index?: number) => {
    if (index !== undefined) {
      setEditingPartIndex(index);
      setPartFormData(parts[index]);
    } else {
      setEditingPartIndex(null);

      // Nommage selon mode
      const defaultName = isParallelWod
        ? `Athlète ${String.fromCharCode(65 + parts.length)}` // A, B, C...
        : `Bloc ${parts.length + 1}`;

      setPartFormData({
        part_number: parts.length + 1,
        name: defaultName,
        type: PartType.FOR_TIME,
        time_cap_minutes: 6,
        // duration_seconds supprimé - uniquement pour type REST
        description: '',
        max_points: 75,
        scoring_type: 'time',
        is_mandatory: true,
        structure: { elements: [] },
        execution_mode: isParallelWod ? ExecutionMode.PARALLEL : ExecutionMode.SEQUENTIAL,
        tracking_mode: TrackingMode.LIVE, // Toujours LIVE, le juge décide quand scorer
        parallel_group: isParallelWod ? 1 : undefined
      });
    }
    setOpenPartDialog(true);
  };

  // L'ordre des parties EST leur numéro : on renumérote 1, 2, 3… après
  // chaque déplacement ou suppression, sinon l'écran et part_number divergent.
  const renumeroter = (liste: WodPart[]) => liste.map((p, i) => ({ ...p, part_number: i + 1 }));

  const handlePartsDragEnd = (r: DropResult) => {
    if (!r.destination || r.destination.index === r.source.index) return;
    const from = r.source.index;
    const to = r.destination.index;
    const liste = [...parts];
    const [deplacee] = liste.splice(from, 1);
    liste.splice(to, 0, deplacee);
    setParts(renumeroter(liste));
    // Les parties dépliées suivent leur partie, pas leur ancienne position.
    setExpandedParts(prev => {
      const ordre = parts.map((_, i) => i);
      const [x] = ordre.splice(from, 1);
      ordre.splice(to, 0, x);
      return new Set(ordre.flatMap((ancien, nouveau) => (prev.has(ancien) ? [nouveau] : [])));
    });
  };

  const handleDeletePart = (index: number) => {
    setParts(renumeroter(parts.filter((_, i) => i !== index)));
    setExpandedParts(prev => new Set(Array.from(prev)
      .filter(i => i !== index)
      .map(i => (i > index ? i - 1 : i))));
  };

  const handleClosePartDialog = () => {
    setOpenPartDialog(false);
    setEditingPartIndex(null);
  };

  const handleSavePart = () => {
    // Auto-assign execution_mode and parallel_group based on WOD mode
    // Toutes les parties parallèles sont LIVE (le juge décide quand scorer via toggle A/B)
    const partToSave = {
      ...partFormData,
      ...(partFormData.type === PartType.REST
        ? { time_cap_minutes: undefined, duration_seconds: partFormData.duration_seconds || 60 }
        : {}),
      execution_mode: isParallelWod ? ExecutionMode.PARALLEL : ExecutionMode.SEQUENTIAL,
      parallel_group: isParallelWod ? 1 : undefined,
      tracking_mode: TrackingMode.LIVE // Toujours LIVE
    };

    if (editingPartIndex !== null) {
      const updatedParts = [...parts];
      updatedParts[editingPartIndex] = partToSave;
      setParts(updatedParts);
    } else {
      setParts([...parts, partToSave]);
    }
    handleClosePartDialog();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'FOR TIME': return <Timer size={16} />;
      case 'AMRAP': return <Repeat size={16} />;
      case 'EMOM': return <Clock size={16} />;
      case 'MAX LOAD': return <TrendingUp size={16} />;
      default: return <Dumbbell size={16} />;
    }
  };

  const getTypeVariant = (type: string): "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "info" => {
    switch (type) {
      case 'FOR TIME': return 'info';
      case 'AMRAP': return 'success';
      case 'EMOM': return 'warning';
      case 'TABATA': return 'destructive';
      case 'MAX LOAD': return 'secondary';
      default: return 'default';
    }
  };

  /** Libellé d'un type de scoring.
   *
   * Il pointait sur des clés empruntées à d'autres écrans, et deux d'entre
   * elles étaient fausses : `common.time` rend « Heure » — une heure
   * d'horloge, alors qu'un For Time se marque sur un temps ÉCOULÉ — et
   * `results.score` rendait « Score » pour un Max Load, qui est un poids.
   * `wods.parts.scoringTypes` porte les bons libellés dans les quatre langues
   * et couvre aussi `distance` et `calories`, que le switch laissait sortir
   * en clé brute. */
  const getScoringLabel = (scoring: string) =>
    t(`wods.parts.scoringTypes.${scoring}`, { defaultValue: scoring });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // selectedEvent vient du contexte EventContext
  const estEvenementCourse = isCourseEvent(selectedEvent?.event_type);
  const itemLabel = estEvenementCourse ? t('nav.courses') : 'WOD';
  // #, nom, description, actions — plus type / time cap / scoring hors course.
  // +1 depuis la colonne « Palier ». Ce nombre pilote le colSpan des lignes
  // dépliées : oublié, le détail se décale d'une colonne.
  const nbColonnes = estEvenementCourse ? 5 : 4;
  const itemLabelPlural = estEvenementCourse ? t('nav.courses') : t('nav.wods');

  // ── Éditeur de WOD (page /admin/wods/new et /admin/wods/:id/edit) ──
  // Une colonne plafonnée à 1152 px : la conversation a ses deux bulles
  // (organisateur à droite, WODEE à gauche) et ne partage plus ce cadre avec
  // la prescription — plus large, une conversation se lit mal.
  const editorContent = (
    <div className="max-w-6xl mx-auto">
      <div className="mb-5">
        <Button variant="link" size="sm" className="h-auto p-0 text-muted-foreground" onClick={handleCloseDialog}>
          ← {t('wods.editor.back', { label: itemLabelPlural })}
        </Button>
        <h1 className="text-2xl font-bold mt-1">
          {estEvenementCourse
            ? (editingWod ? t('wods.editCourse') : t('wods.createCourse'))
            : (editingWod ? t('wods.editWod') : t('wods.createWod'))}
        </h1>
      </div>
      <div className="space-y-2">
          {/* WODEE d'abord : conversation à gauche, prescription à droite */}
          {wodMode === 'ai' && (
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">WODEE</span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {aiMessages.length > 0 ? `${aiMessages.length} msg` : t('ai.organizerAudience')}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{t('ai.chatIntro')}</p>
                  {aiMessages.length > 0 && (
                    <div ref={aiScrollRef} className="max-h-[50vh] overflow-y-auto space-y-3 rounded-lg border border-border bg-muted/30 p-3">
                      {aiMessages.map((m, i) => (
                        <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                          {(() => {
                            const lines = m.content.split('\n');
                            const isLong = lines.length > AI_MSG_COLLAPSE_LINES;
                            const isOpen = expandedAiMessages.has(i);
                            const shown = isLong && !isOpen ? lines.slice(0, AI_MSG_COLLAPSE_LINES).join('\n') : m.content;
                            return (
                              <div
                                className={cn(
                                  'text-sm whitespace-pre-line rounded-lg px-3 py-2 max-w-[75%]',
                                  m.role === 'user'
                                    ? 'bg-primary/15 min-w-[50%]'
                                    : 'bg-background border border-border'
                                )}
                              >
                                {m.role === 'assistant' && (
                                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">WODEE</p>
                                )}
                                {m.role === 'assistant' ? renderChatText(shown) : shown}
                                {isLong && (
                                  <button
                                    type="button"
                                    className="block mt-2 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                                    onClick={() => setExpandedAiMessages(prev => {
                                      const next = new Set(prev);
                                      if (next.has(i)) next.delete(i); else next.add(i);
                                      return next;
                                    })}
                                  >
                                    {isOpen ? t('ai.showLess') : t('ai.showMore', { count: lines.length - AI_MSG_COLLAPSE_LINES })}
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      ))}
                      {aiGenerating && (
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          {t('ai.thinking')}
                        </div>
                      )}
                    </div>
                  )}
                  {aiWarnings.length > 0 && (
                    <Alert>
                      <AlertDescription>
                        <p className="font-semibold mb-1">{t('ai.warningsTitle')}</p>
                        <ul className="list-disc pl-4 space-y-0.5 text-xs">
                          {aiWarnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                  {aiError && (
                    <Alert variant="destructive">
                      <AlertDescription>{aiError}</AlertDescription>
                    </Alert>
                  )}
                  <Textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => {
                      // Entrée envoie ; Maj+Entrée saute une ligne (une feuille
                      // de WOD se colle sur plusieurs lignes).
                      if (e.key === 'Enter' && !e.shiftKey && aiMessages.length > 0) {
                        e.preventDefault();
                        handleAiSend();
                      }
                    }}
                    placeholder={aiMessages.length === 0 ? t('ai.promptPlaceholder') : t('ai.chatReply')}
                    rows={aiMessages.length === 0 ? 9 : 2}
                    disabled={aiGenerating}
                    className="resize-y"
                  />
                  <label className={cn(
                    'flex items-center gap-3 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground cursor-pointer hover:border-primary/60',
                    aiGenerating && 'pointer-events-none opacity-60'
                  )}>
                    <span className="w-7 h-7 rounded-full glass-ring flex items-center justify-center shrink-0">📷</span>
                    <span>{t('ai.photoDrop')}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) handleAiPhoto(f);
                      }}
                    />
                  </label>
                  <div className="flex items-center justify-end gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground mr-auto">{t('ai.enterHint')}</span>
                    {aiMessages.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={handleAiReset} disabled={aiGenerating}>
                        {t('ai.newConversation')}
                      </Button>
                    )}
                    <Button
                      variant={aiMessages.length > 0 ? 'secondary' : 'default'}
                      onClick={handleAiSend}
                      disabled={aiGenerating || !aiPrompt.trim()}
                    >
                      {aiGenerating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('ai.generating')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {aiMessages.length === 0 ? t('ai.sendToWodee') : t('ai.send')}
                        </>
                      )}
                    </Button>
                    {aiMessages.length > 0 && (
                      <Button onClick={handleAiValidate} disabled={aiGenerating}>
                        {t('ai.validate')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-semibold">{t('wods.editor.prescriptionTitle')}</span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('wods.editor.prescriptionHint')}</span>
                  </div>
                  <div className="py-10 text-center text-muted-foreground">
                    <p className="text-lg font-semibold uppercase tracking-wide text-muted-foreground/70 mb-2">
                      {aiMessages.length > 0 ? t('wods.editor.emptyInProgress') : t('wods.editor.emptyTitle')}
                    </p>
                    <p className="text-sm max-w-md mx-auto mb-4">
                      {aiMessages.length > 0 ? t('wods.editor.emptyInProgressText') : t('wods.editor.emptyText')}
                    </p>
                    <Button variant="link" className="text-muted-foreground" onClick={() => setWodMode('form')}>
                      {t('wods.editor.manualLink')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* WOD Form */}
          {wodMode === 'form' && (
          <div className="space-y-6 py-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Bandeau WODEE : replié une fois le WOD validé, rouvrable */}
              {!editingWod && !estEvenementCourse && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-2.5 text-sm">
                  <div className="text-muted-foreground">
                    <span className="font-semibold text-foreground">WODEE</span>
                    {aiMessages.length > 0
                      ? ` · ${formData.name || t('wods.createWod')} · ${t('wods.editor.validatedSummary', { count: coverage.categoryKeys.length || 1 })}`
                      : ` · ${t('wods.editor.wodeeUnused')}`}
                  </div>
                  {aiAvailable !== false && (
                    <Button variant="link" size="sm" className="h-auto p-0 text-muted-foreground" onClick={() => setWodMode('ai')}>
                      {aiMessages.length > 0 ? t('wods.editor.reopenChat') : t('wods.editor.describeToWodee')}
                    </Button>
                  )}
                </div>
              )}

              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="grid grid-cols-[80px,1fr] gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="wod_number_multi">N°</Label>
                      <NumberInput
                        id="wod_number_multi"
                        value={formData.wod_number}
                        onChange={(e) => setFormData({ ...formData, wod_number: Number(e.target.value) })}
                        min={1}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="wod_name_multi">{t('wods.wodName')}</Label>
                      <Input
                        id="wod_name_multi"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={isParallelWod ? t('wods.wodNamePlaceholderParallel') : t('wods.wodNamePlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description_multi">{t('wods.description')}</Label>
                    <Textarea
                      id="description_multi"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={t('wods.descriptionPlaceholder')}
                      rows={2}
                    />
                  </div>

                  {/* Ni statut, ni mystere, ni date de revelation sur cette fiche :
                      la visibilite vit sur la LIGNE du WOD, dans le palier
                      Brouillon / Mystere / Public. Changer une visibilite ne doit
                      pas demander d'ouvrir le WOD, de descendre et d'enregistrer.
                      `wods.status` n'a plus aucun lecteur depuis les paliers : le
                      garder ici offrait un second interrupteur, inerte, sous une
                      phrase qui promettait l'inverse ("les WODs inactifs ne sont
                      pas affiches"). Voir components/wods/WodPalier.tsx. */}
                </CardContent>
              </Card>

              {/* WOD éliminatoire (LOG-219) : accès conditionné par le résultat d'un autre WOD */}
              <Card>
                <CardContent className="pt-6 space-y-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="wod_requires">{t('wods.prerequisite.label')}</Label>
                    <p className="text-xs text-muted-foreground">{t('wods.prerequisite.hint')}</p>
                  </div>
                  <Select
                    value={formData.requires_finished_wod_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, requires_finished_wod_id: v === 'none' ? null : v })}
                  >
                    <SelectTrigger id="wod_requires">
                      <SelectValue placeholder={t('wods.prerequisite.none')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('wods.prerequisite.none')}</SelectItem>
                      {wods
                        .filter(w => w.id && w.id !== editingWod?.id)
                        .sort((a, b) => a.wod_number - b.wod_number)
                        .map(w => (
                          <SelectItem key={w.id} value={w.id!}>
                            {t('wods.prerequisite.option', { number: w.wod_number, name: w.name })}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {formData.requires_finished_wod_id && (
                    <p className="text-xs text-muted-foreground">{t('wods.prerequisite.effect')}</p>
                  )}
                </CardContent>
              </Card>

              {/* Category Tabs + Parts List */}
              <Card>
                <CardContent className="pt-6 space-y-4">
                  {/* Tabs pour les variations par catégorie */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-4 flex-wrap">
                        <h3 className="text-sm font-bold text-foreground">
                          {isParallelWod ? t('wods.blocks.titleParallel') : t('wods.blocks.title')}
                        </h3>
                        <div className="flex items-center gap-2">
                          <Switch
                            id="wod_parallel"
                            checked={isParallelWod}
                            onCheckedChange={setParallelMode}
                          />
                          <Label htmlFor="wod_parallel" className="text-xs text-muted-foreground cursor-pointer">
                            {t('wods.blocks.parallelSwitch')}
                          </Label>
                        </div>
                        {chargeParts.length >= 2 && (
                          <div className="flex items-center gap-2">
                            <Switch
                              id="wod_team_total"
                              checked={classementTotal}
                              onCheckedChange={setClassementTotal}
                            />
                            <Label htmlFor="wod_team_total" className="text-xs text-muted-foreground cursor-pointer">
                              {t('wods.blocks.teamTotalSwitch')}
                            </Label>
                          </div>
                        )}
                      </div>
                      {categories.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setCopySourceKey(selectedCategoryTab); setTargetCategoryId(''); setOpenAddCategoryDialog(true); }}
                                className="text-xs"
                              >
                                <Plus size={14} className="mr-1" />
                                {t('wods.categoryVariations.addVariation')}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t('wods.categoryVariations.addVariationTooltip')}</p>
                            </TooltipContent>
                          </Tooltip>
                      )}
                    </div>

                    {/* Couverture des catégories : dit explicitement qui verra ce WOD */}
                    {categories.length > 0 && (
                      <div className={cn(
                        'flex items-center gap-2 rounded-md border px-3 py-2 text-xs text-muted-foreground',
                        coverage.categoryKeys.length > 0 && coverage.uncovered.length > 0 && !coverage.hasDefault
                          ? 'border-warning/40'
                          : 'border-success/30'
                      )}>
                        <span className={cn(
                          'h-2 w-2 rounded-full shrink-0',
                          coverage.categoryKeys.length > 0 && coverage.uncovered.length > 0 && !coverage.hasDefault ? 'bg-warning' : 'bg-success'
                        )} />
                        {coverage.categoryKeys.length === 0
                          ? t('wods.categoryVariations.coverageSingle', { count: categories.length })
                          : coverage.uncovered.length === 0
                            ? t('wods.categoryVariations.coverageOk', { count: categories.length })
                            : coverage.hasDefault
                              ? t('wods.categoryVariations.coverageDefault', { names: coverage.uncovered.map(c => c.name).join(', ') })
                              : t('wods.categoryVariations.coverageMissingLine', { names: coverage.uncovered.map(c => c.name).join(', ') })}
                      </div>
                    )}

                    {/* Tabs de catégories */}
                    {Object.keys(partsPerCategory).length > 1 && (
                      <div className="flex flex-wrap gap-1 p-1 bg-muted/50 rounded-lg items-center">
                        {Object.keys(partsPerCategory).map((catId) => (
                          <div
                            key={catId}
                            className={cn("flex items-center", draggedVariantKey === catId && "opacity-40")}
                            draggable={Object.keys(partsPerCategory).length > 1}
                            onDragStart={(e) => { setDraggedVariantKey(catId); e.dataTransfer.effectAllowed = 'move'; }}
                            onDragEnd={() => setDraggedVariantKey(null)}
                            onDragOver={(e) => { if (draggedVariantKey) e.preventDefault(); }}
                            onDrop={(e) => { e.preventDefault(); if (draggedVariantKey) handleReorderVariant(draggedVariantKey, catId); setDraggedVariantKey(null); }}
                            title={t('wods.categoryVariations.dragHint')}
                          >
                            {isUnassignedVariantKey(catId) ? (
                              // Variante sans catégorie (default, ou libellé « Sc »…) :
                              // dropdown pour l'assigner
                              <Select onValueChange={(newId) => handleAssignVariantKey(catId, newId)}>
                                <SelectTrigger
                                  className={cn(
                                    "text-xs h-7 w-auto gap-1 border-dashed",
                                    selectedCategoryTab === catId && "shadow-sm bg-primary text-primary-foreground"
                                  )}
                                  onClick={() => handleCategoryTabChange(catId)}
                                >
                                  <SelectValue placeholder={catId === 'default'
                                    ? t('wods.categoryVariations.assignCategory')
                                    : `${catId} → ${t('wods.categoryVariations.assignCategory')}`} />
                                </SelectTrigger>
                                <SelectContent>
                                  {(categories || [])
                                    .filter(c => !Object.keys(partsPerCategory).includes(c.id))
                                    .map(c => (
                                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))
                                  }
                                </SelectContent>
                              </Select>
                            ) : (
                              <Button
                                variant={selectedCategoryTab === catId ? "default" : "ghost"}
                                size="sm"
                                onClick={() => handleCategoryTabChange(catId)}
                                className={cn(
                                  "text-xs h-7",
                                  selectedCategoryTab === catId && "shadow-sm"
                                )}
                              >
                                {getCategoryName(catId)}
                              </Button>
                            )}
                            {Object.keys(partsPerCategory).length > 1 && selectedCategoryTab === catId && catId !== 'default' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveCategoryVariation(catId)}
                                className="h-7 w-7 p-0 ml-0.5 text-destructive hover:text-destructive"
                              >
                                <X size={12} />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Bouton ajouter partie */}
                  <div className="flex justify-end">
                    <Button variant="outline" size="sm" onClick={() => handleOpenPartDialog()}>
                      <Plus size={16} className="mr-1" />
                      {isParallelWod ? t('wods.blocks.addTask') : t('wods.blocks.addBlock')}
                    </Button>
                  </div>

                  {/* Liste des parties */}
                  {parts.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed rounded-lg">
                      <p className="text-muted-foreground mb-2">
                        {isParallelWod ? t('wods.blocks.noTasks') : t('wods.blocks.noBlocks')}
                        {selectedCategoryTab !== 'default' && ` ${t('wods.blocks.forCategory', { name: getCategoryName(selectedCategoryTab) })}`}
                      </p>
                      <Button variant="outline" size="sm" onClick={() => handleOpenPartDialog()}>
                        <Plus size={16} className="mr-1" />
                        {isParallelWod ? t('wods.blocks.addFirstTask') : t('wods.blocks.addFirstBlock')}
                      </Button>
                    </div>
                  ) : (
                    <DragDropContext onDragEnd={handlePartsDragEnd}>
                    <Droppable droppableId="wod-parts">
                    {(dropProvided) => (
                    <div className="space-y-2" ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                      {parts.map((part, index) => {
                        const isPartExpanded = expandedParts.has(index);
                        const partPreview = part.type !== PartType.REST && part.structure?.elements?.length
                          ? WodStructureUtils.generatePreview(part.structure)
                          : '';
                        return (
                        <Draggable key={`part-${index}`} draggableId={`part-${index}`} index={index}>
                        {(drag) => (
                        <div
                          ref={drag.innerRef}
                          {...drag.draggableProps}
                          className="rounded-md bg-muted/50 border"
                        >
                        <div
                          className="flex items-center justify-between p-3 cursor-pointer"
                          onClick={() => togglePartExpansion(index)}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              {...drag.dragHandleProps}
                              onClick={(e) => e.stopPropagation()}
                              className="text-muted-foreground cursor-grab"
                              aria-label={t('wods.blocks.dragToReorder')}
                            >
                              <GripVertical size={16} />
                            </span>
                            {isPartExpanded
                              ? <ChevronDown size={14} className="text-muted-foreground" />
                              : <ChevronRight size={14} className="text-muted-foreground" />}
                            <Badge variant="default">#{part.part_number}</Badge>
                            <span className="font-medium text-sm">{part.name}</span>
                            <Badge variant="outline">
                              {part.type === PartType.REST ? t('wods.blocks.pause') : part.type.toUpperCase().replace(/_/g, ' ')}
                            </Badge>
                            {part.type === PartType.REST ? (
                              <span className="text-xs text-muted-foreground">{formatDureePause(part.duration_seconds)}</span>
                            ) : part.time_cap_minutes && (
                              <span className="text-xs text-muted-foreground">{part.time_cap_minutes} min</span>
                            )}
                            {part.execution_mode === ExecutionMode.PARALLEL && (
                              <Badge variant="outline" className="text-muted-foreground">{t('wods.blocks.parallel')}</Badge>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleOpenPartDialog(index); }}
                              className="h-7 w-7 p-0"
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePart(index);
                              }}
                              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                        {isPartExpanded && (
                          <div className="px-3 pb-3 pl-10 space-y-2">
                            {partPreview ? (
                              <pre className="text-xs font-mono whitespace-pre-wrap text-muted-foreground bg-background/60 rounded-md px-3 py-2 border border-border">
                                {partPreview}
                              </pre>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                {part.type === PartType.REST
                                  ? `${t('wods.blocks.pause')} · ${formatDureePause(part.duration_seconds)}`
                                  : t('wods.blocks.noMovements')}
                              </p>
                            )}
                            {part.description && (
                              <p className="text-xs text-muted-foreground">{part.description}</p>
                            )}
                          </div>
                        )}
                        </div>
                        )}
                        </Draggable>
                        );
                      })}
                      {dropProvided.placeholder}
                    </div>
                    )}
                    </Droppable>
                    </DragDropContext>
                  )}

                  {parts.filter(p => p.type !== PartType.REST && !p.time_cap_minutes).length > 0 && (
                    <Alert variant="warning">
                      <AlertDescription>
                        {t('wods.warnings.partsWithoutTimeCap', { count: parts.filter(p => p.type !== PartType.REST && !p.time_cap_minutes).length })}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleCloseDialog}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSubmit} disabled={saving || parts.length === 0}>
                  {saving && <Loader2 className="animate-spin" />}
                  {editingWod ? t('common.edit') : t('common.create')}
                </Button>
              </div>
          </div>
          )}

          {/* Course Form (simplifié pour événements de type Course) */}
          {wodMode === 'course' && (
          <div className="space-y-6 py-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Alert variant="info">
                <AlertDescription>
                  <strong>{t('wods.course.title')}</strong> — {t('wods.course.hint')}
                </AlertDescription>
              </Alert>

              {/* Infos de base */}
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="grid grid-cols-[80px,1fr] gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="course_number">N°</Label>
                      <NumberInput
                        id="course_number"
                        value={formData.wod_number}
                        onChange={(e) => setFormData({ ...formData, wod_number: Number(e.target.value) })}
                        min={1}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="course_name">{t('wods.course.name')}</Label>
                      <Input
                        id="course_name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={t('wods.course.namePlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="course_description">{t('wods.course.description')}</Label>
                    <Textarea
                      id="course_description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={t('wods.course.descriptionPlaceholder')}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('wods.course.descriptionHint')}
                    </p>
                  </div>

                  {/* Durée maximale d'une vague (2026-10-30) : à l'heure, la vague
                      s'arrête seule, les équipes pas arrivées sont DNF avec leurs
                      distances. Vide = jamais (une course n'a pas de borne par défaut). */}
                  <div className="space-y-2">
                    <Label htmlFor="course_max_wave">{t('wods.course.maxWaveMinutes')}</Label>
                    <NumberInput
                      id="course_max_wave"
                      value={formData.time_cap ?? ''}
                      onChange={(e) => setFormData({ ...formData, time_cap: e.target.value === '' ? undefined : Number(e.target.value) })}
                      min={1}
                      placeholder="110"
                      className="w-32"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('wods.course.maxWaveMinutesHint')}
                    </p>
                  </div>

                  {/* Meme raison que pour un WOD : la visibilite d'une course se
                      regle sur sa LIGNE, via le palier. Voir WodPalier.tsx. */}
                </CardContent>
              </Card>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleCloseDialog}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSubmit} disabled={saving}>
                  {saving && <Loader2 className="animate-spin" />}
                  {editingWod ? t('common.edit') : t('common.create')}
                </Button>
              </div>
          </div>
          )}
      </div>
    </div>
  );

  const auxDialogs = (
    <>
      {/* Part Dialog */}
      <Dialog open={openPartDialog} onOpenChange={setOpenPartDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPartIndex !== null ? t('wods.partDialog.titleEdit', { number: partFormData.part_number }) : t('wods.partDialog.titleNew')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Part Info */}
            <Card>
              <CardContent className="pt-6 space-y-4">
                <h3 className="text-sm font-bold text-foreground">{t('wods.partDialog.infoTitle')}</h3>

                <div className="space-y-2">
                  <Label htmlFor="part_name">{t('wods.partDialog.partName')}</Label>
                  <Input
                    id="part_name"
                    value={partFormData.name}
                    onChange={(e) => setPartFormData({ ...partFormData, name: e.target.value })}
                    placeholder={t('wods.partDialog.partNamePlaceholder')}
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <Label>{t('wods.partDialog.partType')}</Label>
                    <Select
                      value={partFormData.type}
                      onValueChange={(value) => {
                        const newType = value as PartType;
                        // Si AMRAP et structure vide → pré-remplir avec groupe AMRAP
                        let newStructure = partFormData.structure;
                        if (newType === PartType.AMRAP &&
                            (!partFormData.structure || partFormData.structure.elements.length === 0)) {
                          const amrapGroup: WodGroup = {
                            id: `group-${Date.now()}`,
                            type: WodElementType.GROUP,
                            repetitions: 1,
                            isAmrap: true,
                            name: '',
                            children: []
                          };
                          newStructure = { elements: [amrapGroup] };
                        }
                        setPartFormData({
                          ...partFormData,
                          type: newType,
                          structure: newStructure,
                          // Le scoring suit le type : une partie passée de FOR TIME à
                          // MAX LOAD gardait « temps », et le Judge la traitait comme
                          // un For Time (WOD 1 du Friendly Hybrid Contest, 2026-10-22).
                          scoring_type: newType === PartType.MAX_LOAD ? 'weight'
                            : newType === PartType.AMRAP ? 'reps'
                            : newType === PartType.FOR_TIME ? 'time'
                            : partFormData.scoring_type,
                          // Une pause dure duration_seconds : elle ne garde pas le time cap
                          // de 6 min posé par défaut sur une nouvelle partie (affiché
                          // « 6 min » à côté d'une pause de 1 min, constaté le 2026-10-22).
                          ...(newType === PartType.REST
                            ? { time_cap_minutes: undefined, duration_seconds: partFormData.duration_seconds || 60 }
                            : { duration_seconds: undefined, time_cap_minutes: partFormData.time_cap_minutes || 6 }),
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={PartType.FOR_TIME}>FOR TIME</SelectItem>
                        <SelectItem value={PartType.AMRAP}>AMRAP</SelectItem>
                        <SelectItem value={PartType.MAX_LOAD}>MAX LOAD</SelectItem>
                        <SelectItem value={PartType.REST}>REST ({t('wods.blocks.pause')})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {partFormData.type === PartType.REST ? (
                    <div className="space-y-2">
                      {/* Minutes ET secondes : un seul champ en secondes faisait taper
                          « 1 » pour une minute (pause de 1 s). */}
                      <Label htmlFor="duration_min">{t('wods.partDialog.duration')}</Label>
                      <div className="flex items-center gap-2">
                        <NumberInput
                          id="duration_min"
                          className="w-20"
                          value={Math.floor((partFormData.duration_seconds ?? 60) / 60)}
                          onChange={(e) => setPartFormData({
                            ...partFormData,
                            duration_seconds: Number(e.target.value || 0) * 60 + ((partFormData.duration_seconds ?? 60) % 60)
                          })}
                          min={0}
                        />
                        <span className="text-sm text-muted-foreground">min</span>
                        <NumberInput
                          id="duration_sec"
                          className="w-20"
                          value={(partFormData.duration_seconds ?? 60) % 60}
                          onChange={(e) => setPartFormData({
                            ...partFormData,
                            duration_seconds: Math.floor((partFormData.duration_seconds ?? 60) / 60) * 60 + Math.min(59, Number(e.target.value || 0))
                          })}
                          min={0}
                          max={59}
                        />
                        <span className="text-sm text-muted-foreground">s</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="time_cap_part">{t('wods.partDialog.partTimeCap')}</Label>
                        <NumberInput
                          id="time_cap_part"
                          value={partFormData.time_cap_minutes || 6}
                          onChange={(e) => setPartFormData({
                            ...partFormData,
                            time_cap_minutes: Number(e.target.value)
                          })}
                          min={1}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="max_points">{t('wods.partDialog.maxPoints')}</Label>
                        <NumberInput
                          id="max_points"
                          value={partFormData.max_points || 75}
                          onChange={(e) => setPartFormData({
                            ...partFormData,
                            max_points: Number(e.target.value)
                          })}
                          min={1}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{t('wods.partDialog.scoringType')}</Label>
                        <Select
                          value={partFormData.scoring_type || 'time'}
                          onValueChange={(value) => setPartFormData({
                            ...partFormData,
                            scoring_type: value
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="time">{t('wods.partDialog.scoringOptions.time')}</SelectItem>
                            <SelectItem value="reps">{t('wods.partDialog.scoringOptions.reps')}</SelectItem>
                            <SelectItem value="weight">{t('wods.partDialog.scoringOptions.weight')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Configuration Max Load - Only for MAX_LOAD type */}
            {partFormData.type === PartType.MAX_LOAD && (
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Dumbbell className="h-4 w-4" />
                    {t('wods.partDialog.maxLoadConfig')}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('wods.partDialog.maxLoadAttempts')}</Label>
                      <NumberInput
                        min={1}
                        max={10}
                        value={partFormData.max_load_reps || ''}
                        onChange={(e) => setPartFormData({
                          ...partFormData,
                          max_load_reps: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        placeholder="Ex: 3"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('wods.partDialog.maxLoadAttemptsHelp')}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('wods.partDialog.maxLoadScoringMode')}</Label>
                      <Select
                        value={partFormData.max_load_scoring_mode || 'max'}
                        onValueChange={(value) => setPartFormData({
                          ...partFormData,
                          max_load_scoring_mode: value as 'max' | 'total'
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="max">{t('wods.partDialog.maxLoadScoringModes.max')}</SelectItem>
                          <SelectItem value="total">{t('wods.partDialog.maxLoadScoringModes.total')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {t('wods.partDialog.maxLoadScoringHelp')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="part_description">{t('wods.partDialog.descriptionTitle')}</Label>
              <Textarea
                id="part_description"
                value={partFormData.description || ''}
                onChange={(e) => setPartFormData({ ...partFormData, description: e.target.value })}
                placeholder={t('wods.partDialog.descriptionPlaceholder')}
                rows={3}
              />
            </div>

            {/* Part Structure */}
            {partFormData.type !== PartType.REST && (
              <Accordion type="single" collapsible defaultValue="structure" className="w-full">
                <AccordionItem value="structure">
                  <AccordionTrigger className="text-sm font-medium">
                    {t('wods.partDialog.structureTitle')}
                  </AccordionTrigger>
                  <AccordionContent>
                    <WODStructureBuilder
                      structure={partFormData.structure || { elements: [] }}
                      onChange={(newStructure) => setPartFormData({ ...partFormData, structure: newStructure })}
                    />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClosePartDialog}>
              {t('wods.partDialogFooter.cancel')}
            </Button>
            <Button onClick={handleSavePart}>
              {editingPartIndex !== null ? t('wods.partDialogFooter.modify') : t('wods.partDialogFooter.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Variation Dialog — source EN HAUT, cible EN BAS, un
          seul bouton (mockup validé par Bryan le 2026-09-09). « — Vide — »
          est une valeur de la source, pas un second bouton. */}
      <Dialog open={openAddCategoryDialog} onOpenChange={setOpenAddCategoryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('wods.addCategoryDialog.title')}</DialogTitle>
          </DialogHeader>
          {(() => {
            const availableTargets = (categories || []).filter(cat => !getCategoryKeys().includes(cat.id));
            const sourceName = copySourceKey && copySourceKey !== '__empty__' ? getCategoryName(copySourceKey) : null;
            const targetName = availableTargets.find(c => c.id === targetCategoryId)?.name;
            return (
              <>
                <div className="space-y-4 py-2">
                  <p className="text-sm text-muted-foreground">
                    {t('wods.addCategoryDialog.description')}
                  </p>

                  <div className="space-y-2">
                    <Label>{t('wods.addCategoryDialog.source')}</Label>
                    <Select value={copySourceKey || '__empty__'} onValueChange={setCopySourceKey}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.keys(partsPerCategory).map(k => (
                          <SelectItem key={k} value={k}>{getCategoryName(k)}</SelectItem>
                        ))}
                        <SelectItem value="__empty__" className="italic text-muted-foreground">
                          {t('wods.addCategoryDialog.sourceEmpty')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('wods.addCategoryDialog.target')}</Label>
                    {availableTargets.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-2">
                        {t('wods.addCategoryDialog.noCategoriesAvailable')}
                      </p>
                    ) : (
                      <Select value={targetCategoryId} onValueChange={setTargetCategoryId}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('wods.addCategoryDialog.targetPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableTargets.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {targetName && (
                    <p className="text-xs text-muted-foreground">
                      {sourceName
                        ? t('wods.addCategoryDialog.willCopy', { source: sourceName, target: targetName })
                        : t('wods.addCategoryDialog.willStartEmpty', { target: targetName })}
                    </p>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenAddCategoryDialog(false)}>
                    {t('wods.partDialogFooter.cancel')}
                  </Button>
                  <Button
                    disabled={!targetCategoryId}
                    onClick={() => handleAddCategoryVariation(
                      targetCategoryId,
                      copySourceKey && copySourceKey !== '__empty__' ? copySourceKey : null
                    )}
                  >
                    {t('wods.addCategoryDialog.add')}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={coverageConfirm !== null}
        onOpenChange={(o) => { if (!o) setCoverageConfirm(null); }}
        title={t('wods.categoryVariations.coverageTitle')}
        message={coverageConfirm?.message ?? ''}
        confirmLabel={t('wods.categoryVariations.coverageConfirm')}
        loading={saving}
        onConfirm={async () => {
          const partsToSave = coverageConfirm?.parts;
          setCoverageConfirm(null);
          if (partsToSave) await performSave(partsToSave);
        }}
      />

    </>
  );

  if (isEditorRoute) {
    return (
      <div className="p-4 md:p-6">
        {editorContent}
        {auxDialogs}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 pb-4 mb-4 border-b border-border flex-wrap">
        {/* Stats - Gauche */}
        <div className="flex gap-2 items-center flex-wrap">
          {selectedEventId && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Dumbbell size={14} /> {wods.length} {itemLabelPlural}
            </Badge>
          )}
        </div>

        {/* Actions - Droite */}
        <div className="flex gap-2 items-center flex-wrap">
          <Button
            size="sm"
            onClick={() => handleOpenDialog()}
            disabled={!selectedEventId}
          >
            <Plus size={18} />
            <span className="ml-2 whitespace-nowrap">
              {estEvenementCourse ? t('wods.createCourse') : t('wods.createWod')}
            </span>
          </Button>
        </div>
      </div>

      {/* Message si aucun événement sélectionné */}
      {!selectedEventId && (
        <Alert className="mb-4">
          <AlertDescription>
            {t('common.selectEventFirst')}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError('')} className="text-sm underline">{t('common.close')}</button>
          </AlertDescription>
        </Alert>
      )}

      {/* WODs Table - Desktop */}
      {selectedEventId && (
        <>
          <div className="hidden md:block">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-muted-foreground font-medium w-12">#</TableHead>
                      <TableHead className="text-muted-foreground font-medium">{itemLabel}</TableHead>
                      {/* Une course n'a ni type ni time cap ni mode de scoring :
                          son temps est MESURÉ, jamais borné, et le classement
                          se fait toujours au chrono. Ces colonnes n'affichaient
                          qu'un « FOR TIME » forcé, un défaut de 120 min que
                          rien ne lit et un « Heure » constant. */}
                      {estEvenementCourse && <TableHead className="text-muted-foreground font-medium">{t('common.description')}</TableHead>}
                      {/* Le palier se regle SUR LA LIGNE : ouvrir la fiche pour
                          changer une visibilite etait le geste de trop. */}
                      <TableHead className="text-muted-foreground font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {t('wods.palier.colonne')}
                          {/* Trois statuts, ça ne se devine pas — « Mystère » encore
                              moins, puisque son intérêt tient à ce qu'il laisse passer
                              le planning tout en retenant le nom. L'explication vit là
                              où la question se pose. */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label={t('wods.palier.aideTitre')}
                                className="w-4 h-4 rounded-full border border-border text-[9px] font-bold italic text-muted-foreground hover:text-foreground hover:border-muted-foreground transition-colors leading-none"
                              >
                                i
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" align="start" className="max-w-sm p-3 normal-case tracking-normal">
                              <p className="font-semibold text-xs mb-2">{t('wods.palier.aideTitre')}</p>
                              <dl className="grid grid-cols-[70px_1fr] gap-x-3 gap-y-1.5 text-xs">
                                <dt className="font-semibold">{t('wods.palier.draft')}</dt>
                                <dd className="text-muted-foreground">{t('wods.palier.aideDraft')}</dd>
                                <dt className="font-semibold">{t('wods.palier.mystery')}</dt>
                                <dd className="text-muted-foreground">{t('wods.palier.aideMystery')}</dd>
                                <dt className="font-semibold">{t('wods.palier.announced')}</dt>
                                <dd className="text-muted-foreground">{t('wods.palier.aideAnnounced')}</dd>
                                <dt className="font-semibold">{t('wods.palier.public')}</dt>
                                <dd className="text-muted-foreground">{t('wods.palier.aidePublic')}</dd>
                              </dl>
                              <p className="text-xs text-muted-foreground mt-2.5 pt-2.5 border-t border-border">
                                {t('wods.palier.aidePied')}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      </TableHead>
                      <TableHead className="text-muted-foreground font-medium">{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wods.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={nbColonnes} className="text-center py-8">
                          <div className="text-muted-foreground">
                            <p className="text-lg font-medium mb-4">{estEvenementCourse ? t('wods.noCourses') : t('wods.noWods')}</p>
                            <Button variant="outline" onClick={() => handleOpenDialog()}>
                              <Plus size={18} className="mr-2" />
                              {estEvenementCourse ? t('wods.createFirstCourse') : `${t('common.createFirst')} ${itemLabel}`}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      wods.sort((a, b) => a.wod_number - b.wod_number).map((wod) => {
                        const defaultParts = getPrimaryParts(wod);
                        const isMultiPart = defaultParts.length > 1;
                        // Une course ne se déplie pas : le détail montre la structure
                        // de mouvements par catégorie, qui n'existe pas ici.
                        const isExpanded = !estEvenementCourse && wod.id ? expandedWods.has(wod.id!) : false;

                        // Calculer le time cap total (gère parties parallèles correctement)
                        const totalTimeCap = isMultiPart ? calculateTotalTimeCap(defaultParts) : 0;

                        return (
                          <React.Fragment key={wod.id}>
                            <TableRow className={isExpanded ? 'bg-muted/30' : ''}>
                              {/* Le numéro et le nom déplient le détail (structure par
                                  catégorie) sans passer par l'édition. */}
                              <TableCell
                                className={estEvenementCourse ? undefined : 'cursor-pointer'}
                                onClick={() => !estEvenementCourse && wod.id && toggleWodExpansion(wod.id)}
                              >
                                <div className="flex items-center gap-1">
                                  {!estEvenementCourse && wod.id && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleWodExpansion(wod.id!);
                                      }}
                                    >
                                      {isExpanded ? (
                                        <ChevronDown size={14} className="text-muted-foreground" />
                                      ) : (
                                        <ChevronRight size={14} className="text-muted-foreground" />
                                      )}
                                    </Button>
                                  )}
                                  <span>{wod.wod_number}</span>
                                </div>
                              </TableCell>
                              <TableCell
                                className={estEvenementCourse ? undefined : 'cursor-pointer'}
                                onClick={() => !estEvenementCourse && wod.id && toggleWodExpansion(wod.id)}
                              >
                                <div>
                                  <span className="font-semibold">
                                    {wod.name}
                                    {isMultiPart && (
                                      <span className="text-muted-foreground font-normal ml-1">
                                        ({defaultParts.length})
                                      </span>
                                    )}
                                  </span>
                                  {/* Type, time cap et scoring en UNE ligne de méta :
                                      trois façons de décrire la même épreuve ne
                                      méritaient pas trois colonnes. Pas de couleur —
                                      un aplat par type ne code aucune hiérarchie et
                                      attire l'œil sur ce qui varie le moins.
                                      La description n'est plus ici : elle vit dans le
                                      détail déplié, une fois, car elle est globale au
                                      WOD et non à ses variantes. */}
                                  {!estEvenementCourse && (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      {[
                                        getDisplayTypes(wod).join(' + '),
                                        isMultiPart
                                          ? (totalTimeCap > 0 ? `${totalTimeCap} min` : null)
                                          : (getDisplayTimeCap(wod) ? `${getDisplayTimeCap(wod)} min` : null),
                                        getDisplayScorings(wod).map(getScoringLabel).join(' + '),
                                      ].filter(Boolean).join(' · ')}
                                    </p>
                                  )}
                                  {estEvenementCourse && wod.description && (
                                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                      {wod.description}
                                    </p>
                                  )}
                                  {/* WOD éliminatoire (LOG-219) : qui ouvre quoi, et pour qui */}
                                  {(wod as any).requires_finished_wod_id && (
                                    <p className="text-xs text-warning mt-0.5">
                                      {t('wods.prerequisite.badgeRequires', {
                                        name: (wod as any).requires_finished_wod_name || '',
                                        count: ((wod as any).qualified_team_ids || []).length,
                                      })}
                                    </p>
                                  )}
                                  {wods.some(w => (w as any).requires_finished_wod_id === wod.id) && (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      {t('wods.prerequisite.badgeUnlocks', {
                                        names: wods.filter(w => (w as any).requires_finished_wod_id === wod.id).map(w => w.name).join(', '),
                                      })}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                              {/* En course, la description reste une colonne : il n'y
                                  a ni type, ni scoring, ni détail à déplier. */}
                              {estEvenementCourse && (
                                <TableCell>
                                  <span className="text-sm text-muted-foreground">
                                    {wod.description || t('common.noDescription')}
                                  </span>
                                </TableCell>
                              )}
                              <TableCell>
                                <WodPalier wod={wod} onChange={loadWods} />
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {estEvenementCourse && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => navigate(`/admin/wods/${wod.id}/parcours`)}
                                      className="text-muted-foreground hover:text-foreground hover:bg-white/5"
                                    >
                                      {t('courseParts.open')}
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenDialog(wod)}
                                    className="text-muted-foreground hover:text-foreground hover:bg-white/5"
                                  >
                                    <Edit size={18} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(wod)}
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 size={18} />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>

                            {/* Détail déployé : chaque variante de catégorie, ses blocs et
                                leur structure — lecture seule, sans ouvrir l'édition. */}
                            {isExpanded && (
                              <TableRow className="bg-muted/10 border-l-2 border-l-primary/30">
                                <TableCell colSpan={nbColonnes} className="pl-10 py-3">
                                  <div className="space-y-4">
                                    {buildVariantRows(wod).rows.map((row) => (
                                      <div key={`${wod.id}-part-${row.index}`} className="space-y-2">
                                        {row.multiPart && row.part && (() => {
                                          const part = row.part;
                                          const isRest = part.type === PartType.REST;
                                          const isParallel = part.execution_mode === ExecutionMode.PARALLEL || part.parallel_group != null;
                                          let durationDisplay = '';
                                          if (isRest) {
                                            const seconds = part.duration_seconds || 0;
                                            durationDisplay = seconds >= 60 ? `${Math.ceil(seconds / 60)} min` : `${seconds} sec`;
                                          } else if (part.time_cap_minutes) {
                                            durationDisplay = `${part.time_cap_minutes} min`;
                                          }
                                          return (
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className="text-xs text-muted-foreground">#{part.part_number ?? row.index + 1}</span>
                                              <span className="text-sm font-medium">{part.name}</span>
                                              <Badge
                                                variant={isRest ? 'secondary' : getTypeVariant(part.type.toUpperCase().replace(/_/g, ' '))}
                                                className="text-xs"
                                              >
                                                {isRest && <Pause size={10} className="mr-1" />}
                                                {part.type.toUpperCase().replace(/_/g, ' ')}
                                              </Badge>
                                              {durationDisplay && <span className="text-xs text-muted-foreground">{durationDisplay}</span>}
                                              {!isRest && part.scoring_type && (
                                                <Badge variant="outline" className="text-xs">{getScoringLabel(part.scoring_type)}</Badge>
                                              )}
                                              {isParallel && (
                                                <Tooltip>
                                                  <TooltipTrigger>
                                                    <GitBranch size={12} className="text-muted-foreground" />
                                                  </TooltipTrigger>
                                                  <TooltipContent>
                                                    Partie parallèle (groupe {part.parallel_group ?? '?'})
                                                  </TooltipContent>
                                                </Tooltip>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        {/* Une colonne par catégorie ; deux catégories à structure
                                            identique partagent la colonne (mockup B, 2026-09-09). */}
                                        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(210px,1fr))]">
                                          {row.cols.map((col, colIdx) => (
                                            <div key={`${wod.id}-part-${row.index}-col-${colIdx}`} className="space-y-1 min-w-0">
                                              {row.showLabels && (
                                                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                                                  {col.labels.join(' · ')}
                                                </p>
                                              )}
                                              {col.preview ? (
                                                <pre className="text-xs font-mono whitespace-pre-wrap text-muted-foreground bg-background/60 rounded-md px-3 py-2 border border-border">
                                                  {col.preview}
                                                </pre>
                                              ) : (
                                                <p className="text-xs text-muted-foreground italic">{t('wods.blocks.noMovements')}</p>
                                              )}
                                              {/* Une partie hérite de la description du WOD
                                                  à la création : la répéter sous chacune des
                                                  quatre variantes affichait quatre fois la même
                                                  phrase. On ne la montre que si elle DIFFÈRE —
                                                  là, elle apporte quelque chose. */}
                                              {col.part.description &&
                                               col.part.description !== wod.description && (
                                                <p className="text-xs text-muted-foreground">{col.part.description}</p>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {/* La description appartient au WOD, pas à ses variantes :
                                      une fois, sur toute la largeur, détachée des structures
                                      par un filet et un vrai blanc. */}
                                  {wod.description && (
                                    <div className="mt-6 pt-4 border-t border-border">
                                      <p className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                                        {t('common.description')}
                                      </p>
                                      <p className="text-sm text-muted-foreground whitespace-pre-line">
                                        {wod.description}
                                      </p>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* WODs Cards - Mobile */}
          <div className="md:hidden space-y-3">
            {wods.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-lg font-medium mb-4 text-muted-foreground">
                    {estEvenementCourse ? t('wods.noCourses') : t('wods.noWods')}
                  </p>
                  <Button variant="outline" onClick={() => handleOpenDialog()}>
                    <Plus size={18} className="mr-2" />
                    {estEvenementCourse ? t('wods.createFirstCourse') : `${t('common.createFirst')} ${itemLabel}`}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              wods.sort((a, b) => a.wod_number - b.wod_number).map((wod) => (
                <Card key={wod.id}>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="font-bold">
                              #{wod.wod_number}
                            </Badge>
                            <span className="font-semibold">{wod.name}</span>
                          </div>
                          {wod.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {wod.description}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {estEvenementCourse && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/admin/wods/${wod.id}/parcours`)}
                              className="h-8 px-2 text-muted-foreground hover:text-foreground"
                            >
                              {t('courseParts.open')}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(wod)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                          >
                            <Edit size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(wod)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2 text-sm">
                          <span className="text-muted-foreground shrink-0">Type</span>
                          <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                            {getDisplayTypes(wod).map((type, idx) => (
                              <Badge key={idx} variant={getTypeVariant(type)} className="text-xs">
                                {type}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground shrink-0">Time Cap</span>
                          <span className="font-medium">
                            {(() => {
                              const timeCap = getDisplayTimeCap(wod);
                              return timeCap ? `${timeCap} min` : 'Pas de limite';
                            })()}
                          </span>
                        </div>
                        <div className="flex items-start justify-between gap-2 text-sm">
                          <span className="text-muted-foreground shrink-0">Scoring</span>
                          <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                            {getDisplayScorings(wod).map((scoring, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {getScoringLabel(scoring)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={wodToDelete !== null}
        onOpenChange={(o) => { if (!o) setWodToDelete(null); }}
        title={t('common.confirmDeleteTitle')}
        message={t('wods.deleteConfirm', { name: wodToDelete?.name ?? '' })}
        confirmLabel={t('common.delete')}
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default WODs;

/** « 1 min », « 45 s », « 1 min 30 s ». */
function formatDureePause(secondes?: number): string {
  const total = secondes ?? 0;
  const min = Math.floor(total / 60);
  const sec = total % 60;
  if (min && sec) return `${min} min ${sec} s`;
  return min ? `${min} min` : `${sec} s`;
}
